import {Component} from 'react';
import PropTypes from 'prop-types';
import propertyMarker from '../images/property_marker.png';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import {Icon as MaterialUiIcon} from '@mui/material';
import {Icon} from 'semantic-ui-react';
import './ComparableMap.scss';
import bathIcon from 'images/bath-icon.svg';
import bedIcon from 'images/bed-icon.svg';
import carIcon from 'images/car-icon.svg';
import featuredSalesMarker from 'images/featured_sales_marker.png';
import featuredListingsMarker from 'images/featured_listings_marker.png';
import featuredRentalsMarker from 'images/featured_rentals_marker.png';
import Currency from 'components/Currency';
import DisclaimerMark from 'components/DisclaimerMark/DisclaimerMark';
import Optional from 'components/Optional';
import {getIn} from 'lib/immutableObject';
import {checkIfMarkersChanged, fitBounds} from 'lib/MapUtil';

export default class ComparableItemMap extends Component {

  static propTypes = {
    targetLatLng: PropTypes.shape({lat: PropTypes.number.isRequired, lng: PropTypes.number.isRequired}).isRequired,
    center: PropTypes.shape({lat: PropTypes.number.isRequired, lng: PropTypes.number.isRequired}),
    zoom: PropTypes.number,
    markers: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
        sla: PropTypes.string.isRequired,
        beds: PropTypes.number,
        bedsIsEdited: PropTypes.bool,
        baths: PropTypes.number,
        bathsIsEdited: PropTypes.bool,
        cars: PropTypes.number,
        carsIsEdited: PropTypes.bool,
        daysOnMarket: PropTypes.number,
        salePrice: PropTypes.oneOfType([PropTypes.string,PropTypes.number]),
        salePriceIsEdited: PropTypes.bool,
      }).isRequired
    ).isRequired,
    comparableType: PropTypes.string.isRequired,
    onMapZoomChange: PropTypes.func.isRequired,
    onMapCenterChange: PropTypes.func.isRequired,
  };

  componentDidMount() {
    const {targetLatLng, center, zoom} = this.props;
    if (targetLatLng && getIn(window, ['google', 'maps'])) {
      const width = this._mapEl.offsetWidth;
      this._mapEl.style.height = `${width / (490 / 641)}px`;
      const position = window.google.maps.ControlPosition.RIGHT_TOP;
      const map = this._map = new window.google.maps.Map(this._mapEl, {
        center: center || targetLatLng,
        zoom: zoom || 16,
        disableDefaultUI: true,
        scaleControl: true,
        zoomControl: true,
        zoomControlOptions: {
          position
        },
        clickableIcons: false,
      });

      this._targetMarker = new window.google.maps.Marker({
        map,
        position: targetLatLng,
        icon: {
          scaledSize: {width: 39, height: 33},
          url: propertyMarker,
        },
      });

      window.google.maps.event.addListener(map, 'center_changed', this.onCenterChanged);
      window.google.maps.event.addListener(map, 'zoom_changed', this.onZoomChanged);

      this.makeMarkers();
    }
  }

  componentDidUpdate(prevProps) {
    const {center, zoom, markers} = this.props;

    if (this._map && !isEqual(center, prevProps.center)) {
      this._map.setCenter(center);
    }

    if (this._map && zoom !== prevProps.zoom) {
      this._map.setZoom(zoom);
    }

    if (checkIfMarkersChanged(markers, prevProps.markers)) {
      this.makeMarkers();
    }
  }

  componentWillUnmount() {
    if (this._map) {
      window.google.maps.event.clearInstanceListeners(this._map);
      this._markers.forEach(m => m.setMap(null));
      this._targetMarker.setMap(null);
    }
  }

  _markers = [];

  getMarkerUrl = () => {
    return {
      sales: featuredSalesMarker,
      listings: featuredListingsMarker,
      rentals: featuredRentalsMarker,
    }[this.props.comparableType];
  };

  makeMarkers = () => {
    const {markers, targetLatLng} = this.props;

    if (this._map) {
      const markerUrl = this.getMarkerUrl();

      this._markers.forEach(m => m.setMap(null));
      this._markers = markers.map(({label, lat, lng}) => new window.google.maps.Marker({
        map: this._map,
        position: {lat, lng},
        icon: {
          scaledSize: {width: 39, height: 33},
          url: markerUrl,
          labelOrigin: {x: 16, y: 13},
        },
        label: label ? {text: label, color: '#ffffff'} : undefined,
      }));

      const allLatLng = [...markers, targetLatLng];
      const northEast = {
        lat: Math.min(...allLatLng.map(({lat}) => lat)),
        lng: Math.min(...allLatLng.map(({lng}) => lng)),
      };
      const southWest = {
        lat: Math.max(...allLatLng.map(({lat}) => lat)),
        lng: Math.max(...allLatLng.map(({lng}) => lng)),
      };

      const legendHeight = this._mapLegend ? this._mapLegend.offsetHeight : 0;
      const {center, zoom} = fitBounds({
        latLng1: northEast,
        latLng2: southWest,
        dimensions: {width: this._mapEl.offsetWidth, height: this._mapEl.offsetHeight},
        targetPixelsBetweenPoints: Math.min(this._mapEl.offsetWidth, this._mapEl.offsetHeight - legendHeight) - 80,
        padding: {bottom: legendHeight},
      });
      this._map.setCenter(center);
      this._map.setZoom(zoom);
    }
  };

  onCenterChanged = debounce(() => {
    const latLng = this._map.getCenter();
    if (latLng) {
      this.props.onMapCenterChange({lat: latLng.lat(), lng: latLng.lng()});
    }
  }, 500);

  onZoomChanged = debounce(() => {
    this.props.onMapZoomChange(this._map.getZoom());
  }, 500);

  buildLegend = () => {
    const {markers, comparableType} = this.props;
    const markerUrl = this.getMarkerUrl();
    if (markers && markers.length > 0) {
      return (
        <div className="comparable-type-legend" ref={(ref) => this._mapLegend = ref}>
          {markers.map((item, idx) => {
            return (
              <div className="row" key={idx}>
                <div className="sla">
                  <div className="legend-marker-icon">
                    <img src={markerUrl} alt=""/>
                    <span className="marker-label">{item.label}</span>
                  </div>
                    <div className="sla-text">{item.sla}</div>
                </div>

                <div className="centerCol">

                  <div className="left">

                    <span className="beds">
                      <Optional value={item.beds}/>
                      <Icon name="bed"/>
                    </span>

                    <span className="baths">
                      <Optional value={item.baths}/>
                      <Icon name="bath"/>
                    </span>

                    <span className="cars">
                      <Optional value={item.cars}/>
                      <Icon name="car"/>
                    </span>

                    {comparableType === 'listings' &&
                      <span className="dom">
                        <span>DOM: </span>
                        <span className="domString">{item.daysOnMarket}</span>
                      </span>
                    }
                  </div>
                  <div className="salePrice">
                    <Currency currency={item.salePrice}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  buildListedPriceLegend = () => {
    const {markers, comparableType} = this.props;
    const markerUrl = this.getMarkerUrl();
    if (markers && markers.length > 0) {
      return (
        <div className="comparable-type-legend full-width" ref={(ref) => this._mapLegend = ref}>
          <div className="grid-container">
            <div className="grid-header">
              <MaterialUiIcon sx={{ fontSize: '25px' }}>
                <img src={bedIcon} alt="bedrooms icon"/>
              </MaterialUiIcon>
              <MaterialUiIcon sx={{ fontSize: '25px' }}>
                <img src={bathIcon} alt="bathrooms icon"/>
              </MaterialUiIcon>
              <MaterialUiIcon sx={{ fontSize: '25px' }}>
                <img src={carIcon} alt="car spaces icon"/>
              </MaterialUiIcon>
              {comparableType === 'listings' && (
                <span className="days-on-market">
                  <span className="header-string">DOM</span>
                </span>
              )}
            </div>
            {markers.map((item, idx) => {
              return (
                <div className={`grid-row ${comparableType === 'listings' ? '' : 'no-dom'}`} key={idx}>
                  <div className="legend-marker-icon">
                    <img src={markerUrl} alt=""/>
                    <span className="marker-label">{item.label}</span>
                  </div>
                  <div className="sla-text">{item.sla}</div>
                  <span className="stats beds">
                    <Optional value={item.beds}/>
                    {item.bedsIsEdited && <DisclaimerMark/>}
                  </span>
                  <span className="stats baths">
                    <Optional value={item.baths}/>
                    {item.bathsIsEdited && <DisclaimerMark/>}
                  </span>
                  <span className="stats cars">
                    <Optional value={item.cars}/>
                    {item.carsIsEdited && <DisclaimerMark/>}
                  </span>
                  {comparableType === 'listings' && (
                    <span className="stats dom">
                      <span className="domString">{item.daysOnMarket}</span>
                    </span>
                  )}
                  <div className="price">
                    {!isNaN(Number(item.salePrice)) ? (<Currency currency={item.salePrice}/>)
                      : (<Optional value={item.salePrice}/>)}
                    {item.salePriceIsEdited && <DisclaimerMark/>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  };

  render() {
    const {targetLatLng} = this.props;
    return (
      <div className="ComparableMap">
        <div className="map" ref={(ref) => this._mapEl = ref}>
          {(!targetLatLng) && (
            <div className="no-data">This property contains incomplete location data</div>
          )}
        </div>
        { this.buildListedPriceLegend() }
      </div>
    );
  }
}
